'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import { FormTextarea } from '@/app/[locale]/(app)/(workspace)/create/_components/FormTextarea';
import { useImg2VideoForm } from '@/app/[locale]/(app)/(workspace)/create/(videoTool)/img2video/_hooks/use-img2video-form';
import { BaseLineDelete } from '@/app/components/icons/BaseLineDelete';
import { BaseLinePlus } from '@/app/components/icons/BaseLinePlus';
import { Loading } from '@/app/components/icons/Loading';
import { ProductLineLoadingPic } from '@/app/components/icons/ProductLineLoadingPic';
import { ProductLineText } from '@/app/components/icons/ProductLineText';
import { BasicTextarea } from '@/app/components/shared/BasicTextarea';
import { ImageUploader } from '@/app/components/shared/ImageUploader';
import { SettingRadioGroup, type ToggleOption } from '@/app/components/shared/SettingRadioGroup';
import { MODEL_VERSION } from '@/app/constants';
import { useForm, useResizeByResolution } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { FormImagePrompt, ImageUploadFile } from '@/app/types';
import { cn, isOptionAllowed } from '@/app/utils';
import { resizeSourceImage } from '@/app/utils/client';
import { getTargetRouteByFormType, reportProductPageElementClick } from '../utils';
import { FreeToCreateButton } from './FreeToCreateButton';
import { ProductSelect } from './ProductSelect';

interface Q3GeneratorInputProps {
  className?: string;
  activeTab: 'img2video' | 'text2video';
  onTabChange?: (tab: 'img2video' | 'text2video') => void;
  /** 图生视频时长（仅用于展示，不控制全局表单） */
  img2VideoDuration?: number;
  /** 文生视频时长（仅用于展示，不控制全局表单） */
  text2VideoDuration?: number;
  /** 图生视频时长变化回调 */
  onImg2VideoDurationChange?: (duration: number) => void;
  /** 文生视频时长变化回调 */
  onText2VideoDurationChange?: (duration: number) => void;
  /** 隐藏类型切换（仅图生视频/文生视频），不显示 ProductSelect */
  hideTabSwitch?: boolean;
  submitButtonText?: string;
  /** 展示用 prompt：仅展示不同步全局表单，点击提交时再同步并跳转 */
  displayPrompt?: { text: string; image: string };
}

type Q3TabType = 'img2video' | 'text2video';

const MAX_Q3_IMG2V_FRAMES = 2;

export const Q3GeneratorInput: React.FC<Q3GeneratorInputProps> = ({
  className,
  activeTab,
  onTabChange,
  img2VideoDuration = 10,
  text2VideoDuration = 10,
  onImg2VideoDurationChange,
  onText2VideoDurationChange,
  hideTabSwitch = false,
  submitButtonText,
  displayPrompt,
}) => {
  const t = useTranslations();

  // 展示模式：本地状态，不写全局 store，提交时再同步
  const [displayText, setDisplayText] = useState(displayPrompt?.text ?? '');
  const displayImagePrompts = useMemo((): FormImagePrompt[] => {
    if (!displayPrompt?.image?.trim()) return [];
    const url = displayPrompt.image.trim();
    return [
      {
        id: `display-${url.slice(-12)}`,
        status: 'done',
        prompt: {
          sourceImage: url,
          sourceImagePreview: url,
          selectedImage: url,
          selectedImagePreview: url,
        },
      },
    ];
  }, [displayPrompt?.image]);

  const handleImg2VideoDurationChange = (duration: number) => {
    onImg2VideoDurationChange?.(duration);
  };

  const handleText2VideoDurationChange = (duration: number) => {
    onText2VideoDurationChange?.(duration);
  };

  // 获取 updateModelVersion 方法
  const updateModelVersion = useStore((state) => state.updateModelVersion);

  // 图生视频相关
  const { formParams: img2videoFormParams } = useStore((state) => ({
    formParams: state.img2videoFormParams,
  }));
  const { updateTextPrompt: updateImg2VideoPromptBase } = useForm('img2video');

  const updateImg2VideoPrompt = (value: string) => {
    if (value.trim().length > 0) {
      reportProductPageElementClick('input text');
    }
    updateImg2VideoPromptBase(value);
  };
  const { maxImageSize } = useResizeByResolution('img2video');

  const { imagePrompts, validImageCount, handleUploadImage, handleRemoveImage, clearImages, setImages } =
    useImg2VideoForm({
      maxCount: MAX_Q3_IMG2V_FRAMES,
    });

  // 文生视频相关
  const { formParams: text2videoFormParams } = useStore((state) => ({
    formParams: state.text2videoFormParams,
  }));
  const { updateTextPrompt: updateText2VideoPromptBase } = useForm('text2video');

  // 展示模式时不同步、不清空全局表单；非展示模式时初始化清空并设置模型版本
  useEffect(() => {
    if (displayPrompt) {
      setDisplayText(displayPrompt.text);
      updateModelVersion('img2video', MODEL_VERSION.Q3);
      updateModelVersion('text2video', MODEL_VERSION.Q3);
      return;
    }
    updateImg2VideoPromptBase('');
    clearImages();
    updateText2VideoPromptBase('');
    updateModelVersion('img2video', MODEL_VERSION.Q3);
    updateModelVersion('text2video', MODEL_VERSION.Q3);
  }, [displayPrompt?.text, displayPrompt?.image]);

  const updateText2VideoPrompt = (value: string) => {
    if (value.trim().length > 0) {
      reportProductPageElementClick('input text');
    }
    updateText2VideoPromptBase(value);
  };

  const { textPrompt: img2videoTextPrompt } = img2videoFormParams;
  const { textPrompt: text2videoTextPrompt } = text2videoFormParams;

  const clearImg2VideoPrompt = () => {
    updateImg2VideoPrompt('');
  };

  const clearText2VideoPrompt = () => {
    updateText2VideoPrompt('');
  };

  // 图生视频是否有图片正在上传
  const hasImg2VideoUploading = useMemo(() => imagePrompts.some((p) => p?.status === 'uploading'), [imagePrompts]);

  // 处理图片上传（图生视频）
  const handleImg2VideoUpload = (file: ImageUploadFile) => {
    if (file.status === 'uploading') {
      reportProductPageElementClick('upload image');
    }
    handleUploadImage(file);
  };

  // 检查是否可以提交（上传中不可提交）；展示模式下用本地 display 状态
  const canSubmit = useMemo(() => {
    if (displayPrompt && activeTab === 'img2video') {
      return displayText.trim().length > 0 && displayImagePrompts.length > 0;
    }
    if (activeTab === 'img2video') {
      if (hasImg2VideoUploading) return false;
      return validImageCount > 0;
    }
    return text2videoTextPrompt.trim().length > 0;
  }, [
    displayPrompt,
    activeTab,
    displayText,
    displayImagePrompts.length,
    validImageCount,
    text2videoTextPrompt,
    hasImg2VideoUploading,
  ]);

  // 根据当前 tab 获取目标路由
  const targetRoute = useMemo(() => {
    if (activeTab === 'text2video') {
      return '/create/text2video';
    }
    return getTargetRouteByFormType('img2video');
  }, [activeTab]);

  const q3DurationOptions: ToggleOption[] = useMemo(() => {
    return Array.from({ length: 16 }, (_, i) => {
      const duration = (i + 1).toString();
      // 根据 Q3 逻辑判断是否需要会员：11-16 秒需要会员
      const isVipOption = !isOptionAllowed('duration', duration, 'free', MODEL_VERSION.Q3);
      return {
        value: duration,
        label: `${i + 1}`,
        isVipOption,
      };
    });
  }, []);

  const getCurrentDuration = () => {
    const duration = activeTab === 'img2video' ? img2VideoDuration : text2VideoDuration;
    return duration && duration > 0 ? duration.toString() : '10';
  };

  return (
    <div
      className={cn(
        'relative flex min-h-[200px] w-full flex-col justify-between rounded-[20px] bg-[rgba(44,44,45,0.70)] p-4 backdrop-blur-[100px] sm:min-h-[560px]',
        className,
      )}
    >
      <div className="flex items-start gap-4 md:flex-col">
        {/* 图生视频：图片上传区域（展示模式用 displayImagePrompts，否则用全局 imagePrompts） */}
        {activeTab === 'img2video' && (
          <div className="flex flex-wrap gap-2 text-system-white64 text-xs">
            {(displayPrompt ? displayImagePrompts : imagePrompts).slice(0, MAX_Q3_IMG2V_FRAMES).map(
              (prompt, idx) =>
                prompt && (
                  <div key={prompt.id} className="relative flex-shrink-0">
                    <div className="relative aspect-square size-[108px] rounded-8">
                      {prompt.status === 'uploading' ? (
                        <div className="flex aspect-square size-full items-center justify-center rounded-8 bg-system-white/5">
                          <Loading className="animate-spin text-xl" />
                        </div>
                      ) : prompt.prompt ? (
                        <div className="relative aspect-square size-full overflow-hidden rounded-8">
                          <img
                            src={
                              prompt.prompt.selectedImagePreview ||
                              prompt.prompt.sourceImagePreview ||
                              prompt.prompt.sourceImage
                            }
                            alt={`Frame ${idx + 1}`}
                            className="h-full w-full object-cover"
                          />
                          {!displayPrompt && (
                            <button
                              type="button"
                              onClick={() => handleRemoveImage(idx)}
                              className="absolute top-1 right-1 flex size-5 items-center justify-center rounded-full bg-system-white24 text-system-white backdrop-blur-[2px]"
                            >
                              <BaseLineDelete className="size-4 text-xs opacity-80" />
                            </button>
                          )}
                          {MAX_Q3_IMG2V_FRAMES > 1 && (
                            <div className="absolute top-0.5 left-1 z-10 flex size-3 items-center justify-center rounded-full bg-system-black64 text-[10px]">
                              {idx + 1}
                            </div>
                          )}
                        </div>
                      ) : (
                        <ImageUploader
                          maxCount={1}
                          getSelectedImage={(url) => resizeSourceImage({ url, maxImageSize })}
                          onChange={handleImg2VideoUpload}
                          allowTempMode={true}
                        >
                          <div className="flex aspect-square size-full cursor-pointer items-center justify-center gap-0.5 rounded-8 bg-system-white/5 p-1 hover:bg-system-white/10">
                            <BaseLinePlus className="size-4 text-[#3FA4FF]" />
                            <span className="bg-gradient-to-r from-[#53C3FF] to-[#2698FF] bg-clip-text font-semibold text-transparent text-xs">
                              {t('create.char2VideoForm.element.addImages')}
                            </span>
                          </div>
                        </ImageUploader>
                      )}
                    </div>
                  </div>
                ),
            )}
            {/* 添加更多按钮（展示模式不显示） */}
            {!displayPrompt && validImageCount < MAX_Q3_IMG2V_FRAMES && (
              <ImageUploader
                maxCount={MAX_Q3_IMG2V_FRAMES - validImageCount}
                getSelectedImage={(url) => resizeSourceImage({ url, maxImageSize })}
                onChange={handleImg2VideoUpload}
                allowTempMode={true}
              >
                <div className="flex aspect-square size-[108px] cursor-pointer items-center justify-center gap-0.5 rounded-8 bg-system-white/5 p-1 hover:bg-system-white/10">
                  <BaseLinePlus className="size-4 text-[#3FA4FF]" />
                  <span className="bg-gradient-to-r from-[#53C3FF] to-[#2698FF] bg-clip-text font-semibold text-transparent text-xs">
                    {t('create.char2VideoForm.element.addImages')}
                  </span>
                </div>
              </ImageUploader>
            )}
          </div>
        )}

        {/* 文本输入框（展示模式用 displayText，否则用全局 store） */}
        {activeTab === 'img2video' ? (
          <FormTextarea
            className="min-h-0 bg-transparent p-0 backdrop-blur-none"
            taskType="img2video"
            basicTextPrompt={displayPrompt ? displayText : img2videoTextPrompt}
            onCloseProMode={displayPrompt ? setDisplayText : updateImg2VideoPrompt}
            onClearPrompt={displayPrompt ? () => setDisplayText('') : clearImg2VideoPrompt}
            hideProMode={true}
          >
            <BasicTextarea
              placeholder={t('create.img2VideoForm.input.placeholder')}
              textPrompt={displayPrompt ? displayText : img2videoTextPrompt}
              updateTextPrompt={displayPrompt ? setDisplayText : updateImg2VideoPrompt}
              showClearButton={false}
              onClearPrompt={displayPrompt ? () => setDisplayText('') : clearImg2VideoPrompt}
            />
          </FormTextarea>
        ) : (
          <FormTextarea
            className="min-h-0 bg-transparent p-0 backdrop-blur-none"
            taskType="text2video"
            basicTextPrompt={text2videoTextPrompt}
            onCloseProMode={updateText2VideoPrompt}
            onClearPrompt={clearText2VideoPrompt}
            hideProMode={true}
          >
            <BasicTextarea
              placeholder={t('create.text2VideoForm.input.placeholder')}
              textPrompt={text2videoTextPrompt}
              updateTextPrompt={updateText2VideoPrompt}
              showClearButton={false}
              onClearPrompt={clearText2VideoPrompt}
            />
          </FormTextarea>
        )}
      </div>

      <FreeToCreateButton
        disabled={!canSubmit}
        targetRoute={targetRoute}
        buttonText={submitButtonText}
        onBeforeNavigate={
          displayPrompt && activeTab === 'img2video'
            ? () => {
                const text = displayText.trim();
                const imageUrl = displayPrompt.image?.trim();
                updateImg2VideoPromptBase(text);
                clearImages();
                if (imageUrl) {
                  const formImagePrompt: FormImagePrompt = {
                    id: crypto.randomUUID(),
                    status: 'done',
                    prompt: {
                      sourceImage: imageUrl,
                      sourceImagePreview: imageUrl,
                      selectedImage: imageUrl,
                      selectedImagePreview: imageUrl,
                    },
                  };
                  setImages([formImagePrompt]);
                }
                updateModelVersion('img2video', MODEL_VERSION.Q3);
              }
            : undefined
        }
        className="mt-0 sm:flex-col sm:items-end"
      >
        <div className="flex w-full items-center justify-between gap-4 md:flex-col md:items-end">
          <ProductSelect
            className={cn('h-9 w-auto min-w-[170px] rounded-10 bg-system-white/5', hideTabSwitch && 'hidden')}
            value={activeTab}
            onValueChange={(val) => onTabChange?.(val as Q3TabType)}
            options={[
              {
                value: 'img2video',
                label: t('create.tabImg2Video'),
                icon: <ProductLineLoadingPic className="size-4" />,
              },
              {
                value: 'text2video',
                label: t('create.tabText2Video'),
                icon: <ProductLineText className="size-4" />,
              },
            ]}
          />
          <div className="flex items-center gap-4">
            <span className="text-sm text-system-white64">{t('create.createForm.settings.duration.title')}</span>
            <div className="[&_div[role='radiogroup']>div]:!size-8 max-w-[196px] overflow-hidden rounded-10 bg-system-white04 [&_div:not([role='radiogroup'])::after]:hidden [&_div:not([role='radiogroup'])]:bg-transparent">
              <SettingRadioGroup
                value={getCurrentDuration()}
                options={q3DurationOptions}
                onValueChange={(val) => {
                  reportProductPageElementClick('Duration');
                  const duration = Number.parseInt(val, 10);
                  if (activeTab === 'img2video') {
                    handleImg2VideoDurationChange(duration);
                  } else {
                    handleText2VideoDurationChange(duration);
                  }
                }}
                labelClassName="font-normal"
              />
            </div>
          </div>
        </div>
      </FreeToCreateButton>
    </div>
  );
};
